import { useCallback, useEffect, useState } from 'react';
import {
  EngineError,
  getThreads,
  getUnmatched,
  resolveUnmatched,
} from '../../api/engineDashboard';
import { caseTitle, formatDateTime } from '../../lib/labels';
import type { CaseListItem, Thread, UnmatchedItem } from '../../types/dashboard';

interface Props {
  token: string;
  cases: CaseListItem[];
  readOnly: boolean;
  /** Open a case's full view (to check context before routing). */
  onOpenCase: (caseId: string) => void;
  onUnauthorized: () => void;
}

/** A *_collision item was ALREADY routed to the most-recent matching case
    and is only flagged for review; an unknown_sender / dead_reply_token one
    was genuinely not routed. Different jobs, so the UI treats them apart. */
function isAutoRouted(item: UnmatchedItem): boolean {
  return (item.reason ?? '').endsWith('collision') || !!item.metadata?.routed_thread_id;
}

/**
 * The unmatched-comms triage queue: inbound SMS/email the Engine couldn't
 * cleanly route. Items that WERE auto-routed (phone/sender on multiple active
 * cases) are shown as "already routed — confirm"; genuinely unrouted ones get
 * the full route-to-case flow. Nothing inbound is ever dropped.
 */
export function UnmatchedInbox({ token, cases, readOnly, onOpenCase, onUnauthorized }: Props) {
  const [items, setItems] = useState<UnmatchedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getUnmatched(token)
      .then((res) => setItems(res.unmatched))
      .catch((err: unknown) => {
        if (err instanceof EngineError && err.status === 401) {
          onUnauthorized();
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load the unmatched inbox');
      });
  }, [token, onUnauthorized]);

  useEffect(load, [load]);

  const routedCount = (items ?? []).filter(isAutoRouted).length;
  const needsRoutingCount = (items ?? []).length - routedCount;

  return (
    <section className="card">
      <div className="flex flex-wrap items-center gap-2 border-b border-surface-border p-3">
        <div className="section-label">Unmatched inbox</div>
        {items && items.length > 0 && (
          <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-800">
            {items.length}
          </span>
        )}
        {items && items.length > 0 && (
          <span className="text-[11px] text-gray-500">
            {needsRoutingCount > 0 && <span className="font-medium text-amber-700">{needsRoutingCount} need routing</span>}
            {needsRoutingCount > 0 && routedCount > 0 && ' · '}
            {routedCount > 0 && <span className="text-green-700">{routedCount} auto-routed, flagged for review</span>}
          </span>
        )}
        <button type="button" className="btn-secondary ml-auto !py-1 !text-xs" onClick={load}>
          Refresh
        </button>
      </div>

      {error && <p className="p-4 text-sm text-red-600">{error}</p>}
      {!error && !items && <p className="p-4 text-sm text-gray-500">Loading…</p>}
      {items && items.length === 0 && (
        <p className="p-4 text-sm text-gray-500">
          Inbox zero — every inbound message is matched to a case thread.
        </p>
      )}

      {items && items.length > 0 && (
        <ul className="divide-y divide-surface-border">
          {items.map((item) => (
            <UnmatchedRow
              key={item.id}
              item={item}
              token={token}
              cases={cases}
              readOnly={readOnly}
              onOpenCase={onOpenCase}
              onResolved={load}
              onUnauthorized={onUnauthorized}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function UnmatchedRow({
  item,
  token,
  cases,
  readOnly,
  onOpenCase,
  onResolved,
  onUnauthorized,
}: {
  item: UnmatchedItem;
  token: string;
  cases: CaseListItem[];
  readOnly: boolean;
  onOpenCase: (caseId: string) => void;
  onResolved: () => void;
  onUnauthorized: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [caseId, setCaseId] = useState('');
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [threadId, setThreadId] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const routed = isAutoRouted(item);
  const candidateCount = item.metadata?.candidate_count;

  const pickCase = async (id: string) => {
    setCaseId(id);
    setThreadId('');
    setThreads(null);
    if (!id) return;
    try {
      const res = await getThreads(token, id);
      setThreads(res.threads);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError('Could not load threads for that case');
    }
  };

  const act = async (input: { thread_id?: string; status?: 'resolved' | 'ignored' }) => {
    setWorking(true);
    setError(null);
    try {
      await resolveUnmatched(token, item.id, input);
      onResolved();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to resolve');
      setWorking(false);
    }
  };

  return (
    <li className="p-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 font-medium uppercase">
              {item.channel}
            </span>
            <span className="font-mono">{item.from_addr}</span>
            <span>{formatDateTime(item.received_at)}</span>
            {routed ? (
              <span className="rounded-full bg-green-100 px-1.5 py-0.5 font-medium text-green-800">
                ✓ Auto-routed{candidateCount ? ` · sender on ${candidateCount} active cases` : ''}
              </span>
            ) : (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
                Needs routing
              </span>
            )}
          </div>
          {item.subject && <div className="mt-1 text-sm font-medium">{item.subject}</div>}
          <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{item.body}</p>
          {routed && (
            <p className="mt-1 text-[11px] text-gray-500">
              Already delivered to the most recent matching case. Confirm if that’s right, or route it to a different one.
            </p>
          )}
        </div>
        {!readOnly && (
          <div className="flex shrink-0 gap-1.5">
            {routed ? (
              <>
                <button
                  type="button"
                  className="btn-primary !py-1 !text-xs"
                  onClick={() => void act({ status: 'ignored' })}
                  disabled={working}
                  title="The message is already on the right case — clear this flag"
                >
                  Looks right
                </button>
                <button
                  type="button"
                  className="btn-secondary !py-1 !text-xs"
                  onClick={() => setOpen((v) => !v)}
                  disabled={working}
                >
                  Route elsewhere…
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-primary !py-1 !text-xs"
                  onClick={() => setOpen((v) => !v)}
                  disabled={working}
                >
                  Route to case…
                </button>
                <button
                  type="button"
                  className="btn-secondary !py-1 !text-xs"
                  onClick={() => void act({ status: 'ignored' })}
                  disabled={working}
                >
                  Ignore
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {open && !readOnly && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-surface-muted p-2 text-xs">
          <select
            className="rounded-md border border-surface-border px-2 py-1"
            value={caseId}
            onChange={(e) => void pickCase(e.target.value)}
          >
            <option value="">Pick a case…</option>
            {cases.map((c) => (
              <option key={c.id} value={c.id}>
                {caseTitle(c.metadata, c.claims)}
              </option>
            ))}
          </select>
          {caseId && (
            <select
              className="rounded-md border border-surface-border px-2 py-1"
              value={threadId}
              onChange={(e) => setThreadId(e.target.value)}
              disabled={!threads}
            >
              <option value="">{threads ? 'Pick a thread…' : 'Loading threads…'}</option>
              {(threads ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.parties?.name ?? t.shared_side ?? 'Thread'}
                </option>
              ))}
            </select>
          )}
          {caseId && (
            <button
              type="button"
              className="text-brand hover:underline"
              onClick={() => onOpenCase(caseId)}
              title="Open this case to check its threads before routing"
            >
              Open case ↗
            </button>
          )}
          <button
            type="button"
            className="btn-primary !py-1 !text-xs"
            disabled={!threadId || working}
            onClick={() => void act({ thread_id: threadId })}
          >
            {working ? 'Routing…' : 'Route message'}
          </button>
          {threads && threads.length === 0 && (
            <span className="text-gray-500">
              No threads on that case yet — send the party a message first.
            </span>
          )}
        </div>
      )}
      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
    </li>
  );
}
